/**
 * Function domain registry — SPEC-FRONTEND_V4 §10.
 * 15 functions. Safety / Compliance / Planning have full spec content;
 * remaining 12 use seeded content based on the spec template.
 */

export interface KPIData {
  title: string;
  value: string | number;
  unit?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendPercent?: number;
}

export interface PainPointData {
  title: string;
  description: string;
  relatedTools: string[];
}

export interface GISLayerData {
  id: string;
  name: string;
  color?: string;
}

export interface IntelCardData {
  title: string;
  insight: string;
  stat: string;
  action?: string;
}

export interface FunctionDomain {
  key: string;
  name: string;
  icon: string;
  color: string;
  shortDescription: string;
  kpis: KPIData[];
  painPoints: PainPointData[];
  tools: string[];
  gisLayers: GISLayerData[];
  intelCards: IntelCardData[];
  zilmPrompts: string[];
}

export const functions: Record<string, FunctionDomain> = {
  planning: {
    key: 'planning',
    name: 'Planning',
    icon: 'Globe',
    color: 'var(--blue)',
    shortDescription:
      'Long-range infrastructure planning and strategic capital improvement programs',
    kpis: [
      { title: 'LRTP Projects Programmed', value: '247', unit: 'of 312 identified', trend: 'up', trendPercent: 7 },
      { title: 'CIP Development Progress', value: '68', unit: '%', trend: 'up', trendPercent: 12 },
      { title: 'Estimated Investment Need', value: '$4.2B', unit: '2026-2050', trend: 'neutral', trendPercent: 0 },
      { title: 'Stakeholder Engagement Events', value: '12', unit: 'of 15 scheduled', trend: 'up', trendPercent: 5 },
    ],
    painPoints: [
      { title: 'Data Integration for Forecasting', description: 'Travel demand, demographic, and economic data from 8+ sources. Manual reconciliation.', relatedTools: ['calculator', 'scenarios'] },
      { title: 'Scenario Modeling Complexity', description: 'Long-range planning requires testing 20+ scenarios (funding levels, growth patterns). Currently manual.', relatedTools: ['scenarios', 'assessment'] },
      { title: 'Stakeholder Communication', description: 'Difficult to visualize plan impacts for diverse stakeholders (public, elected, developers).', relatedTools: ['gis-view', 'intel-brief'] },
    ],
    tools: ['scenarios', 'calculator', 'gis-view', 'assessment'],
    gisLayers: [
      { id: 'lrtp-corridors', name: 'LRTP Priority Corridors', color: '#1751E8' },
      { id: 'cip-projects', name: 'CIP Projects (2026-2030)', color: '#EBF0FF' },
      { id: 'population-growth', name: 'Projected Population Growth', color: '#0A8A65' },
      { id: 'congestion-zones', name: 'Current Congestion Zones', color: '#A06008' },
    ],
    intelCards: [
      { title: 'LRTP Public Review Phase', insight: 'Plan advanced to Final Draft. 30-day public review period begins April 20, 2026.', stat: 'Apr 20', action: 'View Draft' },
      { title: 'CIP Capacity Constraint', insight: 'Available annual funding ($156M) vs. identified needs ($312M). Shortfall: 50%.', stat: '50%', action: 'View Gap Analysis' },
      { title: 'Scenario Outcomes', insight: 'Scenario A (Conservative) vs. Scenario B (Aggressive): 18% difference in 2050 network performance.', stat: '+18%', action: 'Compare Scenarios' },
    ],
    zilmPrompts: [
      'What data sources should we integrate for more accurate travel demand forecasting?',
      'Generate a public-facing summary of our LRTP priorities and expected outcomes',
      'Compare the equity impacts of our 3 funding scenarios across different regions',
      'Create a stakeholder briefing memo on CIP project sequencing for the next 5 years',
    ],
  },

  'policy-reg': {
    key: 'policy-reg',
    name: 'Policy & Reg',
    icon: 'BookOpen',
    color: 'var(--teal)',
    shortDescription:
      'Track regulatory policy changes and agency rule implementation',
    kpis: [
      { title: 'Active Policy Items', value: '42', unit: 'under review', trend: 'up', trendPercent: 4 },
      { title: 'Rules Implemented YTD', value: '18', unit: 'of 22 planned', trend: 'up', trendPercent: 9 },
      { title: 'Pending Federal Rules', value: '7', unit: 'awaiting comment', trend: 'neutral', trendPercent: 0 },
      { title: 'Policy Compliance Rate', value: '96', unit: '%', trend: 'up', trendPercent: 2 },
    ],
    painPoints: [
      { title: 'Rule Change Tracking', description: 'New federal and state rules emerge weekly with no consolidated feed.', relatedTools: ['intel-feed', 'intel-brief'] },
      { title: 'Impact Analysis', description: 'Hard to quantify downstream operational impact of proposed rules.', relatedTools: ['assessment', 'scenarios'] },
      { title: 'Comment Period Management', description: 'Missing comment windows on federal rulemaking costs influence.', relatedTools: ['workflow', 'intel-feed'] },
    ],
    tools: ['intel-feed', 'intel-brief', 'assessment', 'workflow'],
    gisLayers: [
      { id: 'policy-jurisdictions', name: 'Policy Jurisdictions', color: '#0A8A65' },
      { id: 'regulated-corridors', name: 'Regulated Corridors', color: '#E0F5EE' },
    ],
    intelCards: [
      { title: 'New USDOT NPRM', insight: 'Notice of Proposed Rulemaking on bridge inspection cycles — comment window 45 days.', stat: '45d', action: 'Open Brief' },
      { title: 'State Rule Effective', insight: 'Updated work-zone traffic control standard takes effect May 1, 2026.', stat: 'May 1', action: 'View Rule' },
      { title: 'Policy Brief Queue', insight: '3 policy briefs drafted awaiting executive review.', stat: '3', action: 'Review Queue' },
    ],
    zilmPrompts: [
      'Summarize the most impactful policy changes this quarter',
      'Draft a public comment on the proposed FHWA rule',
      'Identify rules with the largest operational impact',
      'Compare our policy posture against peer state DOTs',
    ],
  },

  budgeting: {
    key: 'budgeting',
    name: 'Budgeting',
    icon: 'DollarSign',
    color: 'var(--blue)',
    shortDescription:
      'Capital and operating budget development, execution, and monitoring',
    kpis: [
      { title: 'Budget Obligated YTD', value: '62', unit: '% of $1.8B', trend: 'up', trendPercent: 5 },
      { title: 'Variance from Plan', value: '-2.4', unit: '%', trend: 'down', trendPercent: 1 },
      { title: 'Carryover from FY25', value: '$148M', unit: '', trend: 'neutral', trendPercent: 0 },
      { title: 'Budget Revisions Pending', value: '6', unit: 'requests', trend: 'up', trendPercent: 2 },
    ],
    painPoints: [
      { title: 'Forecast Accuracy', description: 'Construction inflation and schedule slips erode budget accuracy.', relatedTools: ['calculator', 'scenarios'] },
      { title: 'Fund Source Complexity', description: 'Blending federal, state, and local funds within rules is time consuming.', relatedTools: ['calculator', 'assessment'] },
      { title: 'Mid-year Reallocation', description: 'Reallocating funds mid-year is manual and error prone.', relatedTools: ['workflow', 'calculator'] },
    ],
    tools: ['calculator', 'scenarios', 'workflow', 'assessment'],
    gisLayers: [
      { id: 'budget-districts', name: 'Budget Districts', color: '#1751E8' },
      { id: 'funded-projects', name: 'Funded Projects (FY26)', color: '#EBF0FF' },
    ],
    intelCards: [
      { title: 'Q2 Obligation Pace', insight: 'Tracking 3% ahead of FY25 obligation pace at same point.', stat: '+3%', action: 'View Detail' },
      { title: 'Revision Batch Due', insight: 'Batch #4 of FY26 revisions due for board approval May 10.', stat: 'May 10', action: 'Prep Batch' },
      { title: 'Federal Reimbursement', insight: '$62M federal reimbursements pending — avg cycle 42 days.', stat: '$62M', action: 'Track' },
    ],
    zilmPrompts: [
      'Forecast end-of-year obligation rate under current pace',
      'Identify projects at risk of fund lapse',
      'Summarize variance drivers across divisions',
      'Draft a reallocation memo for Q3 board packet',
    ],
  },

  grants: {
    key: 'grants',
    name: 'Grants',
    icon: 'Award',
    color: 'var(--teal)',
    shortDescription:
      'Discretionary grant pursuit, award management, and reporting',
    kpis: [
      { title: 'Active Grant Awards', value: '19', unit: '', trend: 'up', trendPercent: 3 },
      { title: 'Pipeline Value', value: '$312M', unit: 'applications submitted', trend: 'up', trendPercent: 14 },
      { title: 'Award Win Rate', value: '38', unit: '%', trend: 'up', trendPercent: 6 },
      { title: 'Reporting On-time', value: '97', unit: '%', trend: 'neutral', trendPercent: 0 },
    ],
    painPoints: [
      { title: 'Opportunity Scanning', description: 'Federal grant opportunities appear across 20+ portals.', relatedTools: ['intel-feed', 'intel-brief'] },
      { title: 'BCA Quality', description: 'Benefit-cost analyses for discretionary grants vary by author.', relatedTools: ['calculator', 'assessment'] },
      { title: 'Reporting Burden', description: 'Quarterly reporting for each active grant is manual.', relatedTools: ['workflow', 'assessment'] },
    ],
    tools: ['intel-feed', 'calculator', 'assessment', 'workflow'],
    gisLayers: [
      { id: 'grant-projects', name: 'Grant-funded Projects', color: '#0A8A65' },
      { id: 'equity-zones', name: 'Equity Priority Zones', color: '#E0F5EE' },
    ],
    intelCards: [
      { title: 'RAISE NOFO Open', insight: 'USDOT RAISE FY26 NOFO opened with $1.5B available. Deadline July 15.', stat: 'Jul 15', action: 'Open NOFO' },
      { title: 'Bridge Formula Allocation', insight: 'State bridge formula allocation confirmed at $412M.', stat: '$412M', action: 'View' },
      { title: 'Reporting Due Soon', insight: '5 grants have quarterly reports due within 10 days.', stat: '5', action: 'Open Tasks' },
    ],
    zilmPrompts: [
      'Identify grant opportunities aligned with our bridge program',
      'Draft a RAISE application outline for the Route 9 project',
      'Summarize performance metrics across our active awards',
      'Generate talking points for a grant stewardship briefing',
    ],
  },

  procurement: {
    key: 'procurement',
    name: 'Procurement',
    icon: 'ShoppingCart',
    color: 'var(--blue)',
    shortDescription:
      'Solicitation, contracting, and vendor performance management',
    kpis: [
      { title: 'Active Solicitations', value: '34', unit: '', trend: 'up', trendPercent: 4 },
      { title: 'Avg Time-to-Award', value: '112', unit: 'days', trend: 'down', trendPercent: 6 },
      { title: 'DBE Participation', value: '14.2', unit: '%', trend: 'up', trendPercent: 1 },
      { title: 'Protests YTD', value: '2', unit: '', trend: 'down', trendPercent: 50 },
    ],
    painPoints: [
      { title: 'Bid Competition', description: 'Low bidder counts on specialty work driving costs up.', relatedTools: ['scenarios', 'intel-feed'] },
      { title: 'Spec Standardization', description: 'Inconsistent specs across districts create bidder confusion.', relatedTools: ['workflow', 'assessment'] },
      { title: 'Vendor Performance Tracking', description: 'Past performance data not centrally captured.', relatedTools: ['assessment', 'workflow'] },
    ],
    tools: ['workflow', 'assessment', 'calculator', 'intel-feed'],
    gisLayers: [
      { id: 'active-contracts', name: 'Active Contracts', color: '#1751E8' },
      { id: 'dbe-firms', name: 'DBE Firm Footprint', color: '#EBF0FF' },
    ],
    intelCards: [
      { title: 'Protest Risk Flag', insight: 'Solicitation #26-0142 drew 1 bid — single-bid review required.', stat: '1 bid', action: 'Review' },
      { title: 'Board Award Batch', insight: '7 contracts queued for May board award.', stat: '7', action: 'Open Batch' },
      { title: 'DBE Goal Pace', insight: 'On pace to meet 14% annual DBE goal.', stat: 'On pace', action: 'Report' },
    ],
    zilmPrompts: [
      'Identify upcoming solicitations at risk of single-bid outcomes',
      'Summarize vendor past performance for bridge painting',
      'Draft a scope standardization memo for district engineers',
      'Compare our time-to-award against national benchmarks',
    ],
  },

  compliance: {
    key: 'compliance',
    name: 'Compliance',
    icon: 'CheckCircle',
    color: 'var(--green)',
    shortDescription:
      'Track regulatory compliance across federal, state, and local requirements',
    kpis: [
      { title: 'Compliance Score', value: '94', unit: '%', trend: 'up', trendPercent: 3 },
      { title: 'Outstanding Audit Findings', value: '7', unit: 'of 145 resolved', trend: 'down', trendPercent: 15 },
      { title: 'Grant Compliance Rate', value: '98.5', unit: '%', trend: 'neutral', trendPercent: 0 },
      { title: 'Reporting Timeliness', value: '100', unit: '% on-time submissions', trend: 'neutral', trendPercent: 0 },
    ],
    painPoints: [
      { title: 'Multi-Jurisdictional Requirements', description: 'Managing conflicting federal, state, and local rules across 12 agencies. Documentation scattered.', relatedTools: ['assessment', 'calculator'] },
      { title: 'Audit Preparation Burden', description: 'Manual compilation of compliance evidence from 50+ data sources. Takes 6+ weeks per audit.', relatedTools: ['workflow', 'intel-brief'] },
      { title: 'Policy Update Dissemination', description: 'No automated way to track and cascade new regulations to affected projects and staff.', relatedTools: ['intel-feed', 'workflow'] },
    ],
    tools: ['assessment', 'workflow', 'intel-feed', 'calculator'],
    gisLayers: [
      { id: 'jurisdiction-boundaries', name: 'Jurisdiction Boundaries', color: '#236809' },
      { id: 'regulatory-zones', name: 'Regulatory Zones (Wetlands, etc)', color: '#E8F4DC' },
      { id: 'permit-sites', name: 'Active Permits', color: '#0A8A65' },
    ],
    intelCards: [
      { title: 'New Regulations Effective', insight: 'USDOT Rule 23 CFR Part 645 updated effective 04/15/2026 — impacts air quality reporting', stat: '1', action: 'View Full Rule' },
      { title: 'Upcoming Audit Schedule', insight: 'DOT Civil Rights Compliance audit scheduled for June 15-19. Full documentation due May 15.', stat: 'May 15', action: 'Prep Checklist' },
      { title: 'Grant Compliance Status', insight: 'All 18 active federal grants in compliance. 1 state grant under review for administrative capacity.', stat: '18/19', action: 'View Details' },
    ],
    zilmPrompts: [
      'Summarize the key requirements from the latest USDOT guidelines for our agency',
      'Create a compliance tracking matrix for our current grant portfolio',
      'What are the reporting deadlines we need to track for Q2 and Q3?',
      'Draft a memo explaining new federal rule changes to our field teams',
    ],
  },

  safety: {
    key: 'safety',
    name: 'Safety',
    icon: 'AlertTriangle',
    color: 'var(--red)',
    shortDescription:
      'Monitor and manage worker and public safety incidents',
    kpis: [
      { title: 'Lost Time Incidents', value: '12', unit: 'YTD', trend: 'down', trendPercent: 8 },
      { title: 'Safety Audits Completed', value: '156', unit: 'of 200', trend: 'up', trendPercent: 5 },
      { title: 'Incident Rate (per 200k hrs)', value: '2.3', unit: 'national avg: 3.1', trend: 'down', trendPercent: 26 },
      { title: 'Days Without Incident (Avg)', value: '47', unit: 'days', trend: 'up', trendPercent: 12 },
    ],
    painPoints: [
      { title: 'Incident Reporting Delays', description: 'Average 72-hour delay in incident documentation. Manual data entry causing inconsistencies.', relatedTools: ['workflow', 'assessment'] },
      { title: 'Work Zone Visibility', description: 'Crews lack real-time visibility into hazard zones during construction. GIS data outdated.', relatedTools: ['gis-view', 'intel-feed'] },
      { title: 'Training Compliance Gaps', description: 'No centralized tracking of safety certifications across contractors and agency staff.', relatedTools: ['assessment', 'workflow'] },
    ],
    tools: ['gis-view', 'assessment', 'workflow', 'intel-brief'],
    gisLayers: [
      { id: 'work-zones', name: 'Active Work Zones', color: '#A06008' },
      { id: 'incident-hotspots', name: 'Incident Hotspots (12mo)', color: '#C92B2B' },
      { id: 'construction-corridors', name: 'Construction Corridors', color: '#FEF3E2' },
    ],
    intelCards: [
      { title: 'Critical Incidents This Week', insight: 'One near-miss reported in Bridge Rehab Project (PM-2024-045)', stat: '1', action: 'Review Incident Log' },
      { title: 'Upcoming Safety Audits', insight: '8 routine audits scheduled across 4 regions in Q2', stat: '8', action: 'View Schedule' },
      { title: 'Training Completion Rate', insight: '94% of field staff completed mandatory safety training by deadline', stat: '94%', action: 'View Compliance Report' },
    ],
    zilmPrompts: [
      'What are the top 3 safety risks in the bridge rehabilitation program?',
      'Generate a week-by-week safety checklist for the new transit station project',
      'Compare incident rates across our 5 regional operations. Which region needs support?',
      'Draft a memo on Q2 safety audit findings',
    ],
  },

  'risk-mgmt': {
    key: 'risk-mgmt',
    name: 'Risk Mgmt',
    icon: 'Shield',
    color: 'var(--amber)',
    shortDescription:
      'Enterprise risk identification, scoring, and mitigation',
    kpis: [
      { title: 'Active Risks Tracked', value: '84', unit: '', trend: 'up', trendPercent: 3 },
      { title: 'High-severity Risks', value: '11', unit: '', trend: 'down', trendPercent: 8 },
      { title: 'Mitigations On Schedule', value: '78', unit: '%', trend: 'up', trendPercent: 4 },
      { title: 'Risk Exposure', value: '$96M', unit: 'weighted', trend: 'neutral', trendPercent: 0 },
    ],
    painPoints: [
      { title: 'Siloed Risk Registers', description: 'Each division keeps separate risk logs with different scoring.', relatedTools: ['assessment', 'workflow'] },
      { title: 'Emerging Risk Detection', description: 'Hard to spot emerging risks in external feeds.', relatedTools: ['intel-feed', 'scenarios'] },
      { title: 'Scenario Planning', description: 'Limited ability to run cascading risk scenarios.', relatedTools: ['scenarios', 'calculator'] },
    ],
    tools: ['assessment', 'scenarios', 'intel-brief', 'workflow'],
    gisLayers: [
      { id: 'climate-exposure', name: 'Climate Exposure', color: '#A06008' },
      { id: 'asset-criticality', name: 'Asset Criticality', color: '#FEF3E2' },
    ],
    intelCards: [
      { title: 'Weather Advisory', insight: 'Severe storm forecast impacts 7 active work zones this week.', stat: '7', action: 'Open Advisory' },
      { title: 'Top Risk Reviewed', insight: 'Bridge scour risk on corridor 47 rescored to High.', stat: 'High', action: 'View Risk' },
      { title: 'Mitigation Due', insight: '4 mitigation actions due this month.', stat: '4', action: 'Open Tasks' },
    ],
    zilmPrompts: [
      'Summarize our top 5 risks by exposure value',
      'Identify emerging risks from recent intel feed items',
      'Draft a scenario for 2-foot storm surge impact on coastal assets',
      'Recommend mitigation sequencing for high-severity risks',
    ],
  },

  projects: {
    key: 'projects',
    name: 'Projects',
    icon: 'Briefcase',
    color: 'var(--blue)',
    shortDescription:
      'Capital project delivery across design, construction, and closeout',
    kpis: [
      { title: 'Active Projects', value: '312', unit: '', trend: 'up', trendPercent: 4 },
      { title: 'On-schedule %', value: '74', unit: '%', trend: 'up', trendPercent: 3 },
      { title: 'On-budget %', value: '82', unit: '%', trend: 'neutral', trendPercent: 0 },
      { title: 'Avg Change Orders', value: '3.4', unit: '% of contract', trend: 'down', trendPercent: 6 },
    ],
    painPoints: [
      { title: 'Schedule Slippage', description: 'Critical path slips ripple through portfolio.', relatedTools: ['workflow', 'scenarios'] },
      { title: 'Change Order Volume', description: 'High change order rates on complex urban projects.', relatedTools: ['calculator', 'assessment'] },
      { title: 'Stakeholder Coordination', description: 'Utility and ROW coordination delays cause rework.', relatedTools: ['workflow', 'gis-view'] },
    ],
    tools: ['workflow', 'calculator', 'gis-view', 'assessment'],
    gisLayers: [
      { id: 'active-projects', name: 'Active Projects', color: '#1751E8' },
      { id: 'row-acquisition', name: 'ROW Acquisition Status', color: '#EBF0FF' },
    ],
    intelCards: [
      { title: 'Milestone Slips', insight: '4 projects missed milestone in last 30 days.', stat: '4', action: 'View List' },
      { title: 'Substantial Completions', insight: '6 projects reached substantial completion this quarter.', stat: '6', action: 'Review' },
      { title: 'Change Order Watch', insight: 'Route 9 bridge replacement at 8% change orders — flag for review.', stat: '8%', action: 'Open Project' },
    ],
    zilmPrompts: [
      'Identify projects at risk of schedule slippage next quarter',
      'Summarize change order drivers across the portfolio',
      'Draft a milestone recovery plan for Route 9 bridge',
      'Compare our delivery performance against FHWA benchmarks',
    ],
  },

  engineering: {
    key: 'engineering',
    name: 'Engineering',
    icon: 'Tool',
    color: 'var(--amber)',
    shortDescription:
      'Design standards, technical reviews, and engineering decisions',
    kpis: [
      { title: 'Design Reviews in Queue', value: '28', unit: '', trend: 'up', trendPercent: 4 },
      { title: 'Avg Review Cycle', value: '14', unit: 'days', trend: 'down', trendPercent: 9 },
      { title: 'Standards Updates YTD', value: '9', unit: '', trend: 'up', trendPercent: 3 },
      { title: 'Value Engineering Savings', value: '$11.2M', unit: 'YTD', trend: 'up', trendPercent: 18 },
    ],
    painPoints: [
      { title: 'Reviewer Capacity', description: 'Senior engineering reviewers overloaded.', relatedTools: ['workflow', 'assessment'] },
      { title: 'Standards Drift', description: 'District variations in applying standards.', relatedTools: ['assessment', 'intel-brief'] },
      { title: 'BIM/Model Interop', description: 'Inconsistent model handoffs from consultants.', relatedTools: ['gis-view', 'workflow'] },
    ],
    tools: ['gis-view', 'workflow', 'calculator', 'assessment'],
    gisLayers: [
      { id: 'design-corridors', name: 'Design Corridors', color: '#A06008' },
      { id: 'survey-control', name: 'Survey Control Network', color: '#FEF3E2' },
    ],
    intelCards: [
      { title: 'Review Queue Age', insight: 'Oldest review in queue is 24 days old.', stat: '24d', action: 'Open Queue' },
      { title: 'VE Study Opportunity', insight: 'Route 22 interchange flagged for value engineering study.', stat: '1', action: 'Launch Study' },
      { title: 'Standard Published', insight: 'Updated bridge deck overlay standard published last week.', stat: 'New', action: 'View' },
    ],
    zilmPrompts: [
      'Identify design reviews aging past SLA',
      'Summarize recent standards updates for district engineers',
      'Recommend VE study candidates from upcoming projects',
      'Draft a memo on BIM submission expectations for consultants',
    ],
  },

  operations: {
    key: 'operations',
    name: 'Operations',
    icon: 'Truck',
    color: 'var(--amber)',
    shortDescription:
      'Day-to-day operations, maintenance, and traffic management',
    kpis: [
      { title: 'Avg Incident Response', value: '18', unit: 'minutes', trend: 'down', trendPercent: 7 },
      { title: 'Pothole Repairs YTD', value: '24,812', unit: '', trend: 'up', trendPercent: 6 },
      { title: 'Signal Uptime', value: '99.1', unit: '%', trend: 'neutral', trendPercent: 0 },
      { title: 'Winter Events Managed', value: '17', unit: 'this season', trend: 'up', trendPercent: 3 },
    ],
    painPoints: [
      { title: 'Incident Detection', description: 'Secondary incident rates remain elevated.', relatedTools: ['gis-view', 'intel-feed'] },
      { title: 'Maintenance Backlog', description: 'Pavement preservation backlog growing.', relatedTools: ['assessment', 'calculator'] },
      { title: 'Fleet Availability', description: 'Fleet readiness dips below target in peak season.', relatedTools: ['workflow', 'assessment'] },
    ],
    tools: ['gis-view', 'workflow', 'intel-feed', 'assessment'],
    gisLayers: [
      { id: 'maintenance-zones', name: 'Maintenance Zones', color: '#A06008' },
      { id: 'real-time-incidents', name: 'Real-time Incidents', color: '#C92B2B' },
    ],
    intelCards: [
      { title: 'Incident Load', insight: 'Above-average incident volume on corridor 3 today.', stat: '+22%', action: 'View Map' },
      { title: 'Snow Readiness', insight: 'Fleet readiness at 88% with snow forecast Friday.', stat: '88%', action: 'Prep' },
      { title: 'Work Order Queue', insight: '412 open work orders > 30 days old.', stat: '412', action: 'Open Queue' },
    ],
    zilmPrompts: [
      'Summarize today\'s operational hot spots',
      'Identify maintenance backlog hotspots by district',
      'Draft a winter ops readiness briefing',
      'Recommend fleet pre-positioning for forecast storm',
    ],
  },

  assets: {
    key: 'assets',
    name: 'Assets',
    icon: 'Box',
    color: 'var(--slate)',
    shortDescription:
      'Asset inventory, condition monitoring, and lifecycle planning',
    kpis: [
      { title: 'Bridges Inventoried', value: '1,284', unit: '', trend: 'neutral', trendPercent: 0 },
      { title: 'Avg Bridge Condition', value: '6.2', unit: 'NBI rating', trend: 'up', trendPercent: 1 },
      { title: 'Pavement PCI', value: '74', unit: 'network average', trend: 'down', trendPercent: 2 },
      { title: 'Assets Past Useful Life', value: '9.4', unit: '%', trend: 'up', trendPercent: 2 },
    ],
    painPoints: [
      { title: 'Condition Data Freshness', description: 'Inspection cycles lag recommended frequency.', relatedTools: ['assessment', 'workflow'] },
      { title: 'Lifecycle Optimization', description: 'Treatment timing decisions are inconsistent.', relatedTools: ['calculator', 'scenarios'] },
      { title: 'Inventory Accuracy', description: 'Small-asset inventories (signs, markings) incomplete.', relatedTools: ['gis-view', 'assessment'] },
    ],
    tools: ['gis-view', 'calculator', 'assessment', 'workflow'],
    gisLayers: [
      { id: 'bridge-inventory', name: 'Bridge Inventory', color: '#58687A' },
      { id: 'pavement-network', name: 'Pavement Network', color: '#F8F9FB' },
    ],
    intelCards: [
      { title: 'Critical Bridges', insight: '14 bridges rated structurally deficient.', stat: '14', action: 'View List' },
      { title: 'Pavement Decline', insight: 'District 3 PCI declined 4 points year over year.', stat: '-4', action: 'Analyze' },
      { title: 'Inspection Queue', insight: '82 bridge inspections scheduled next 60 days.', stat: '82', action: 'Schedule' },
    ],
    zilmPrompts: [
      'Identify bridges needing replacement within 5 years',
      'Optimize pavement treatment schedule within budget',
      'Summarize asset condition trends by district',
      'Draft a capital renewal argument for the FY27 budget',
    ],
  },

  technology: {
    key: 'technology',
    name: 'Technology',
    icon: 'Cpu',
    color: 'var(--blue)',
    shortDescription:
      'IT and operational technology systems, data platforms, and cybersecurity',
    kpis: [
      { title: 'System Uptime', value: '99.6', unit: '%', trend: 'neutral', trendPercent: 0 },
      { title: 'Open Cyber Findings', value: '5', unit: 'high/critical', trend: 'down', trendPercent: 4 },
      { title: 'Data Platform Usage', value: '1.2k', unit: 'weekly active users', trend: 'up', trendPercent: 12 },
      { title: 'Modernization Projects', value: '9', unit: 'in flight', trend: 'up', trendPercent: 2 },
    ],
    painPoints: [
      { title: 'Legacy System Burden', description: 'Several mission systems are past end-of-life.', relatedTools: ['assessment', 'scenarios'] },
      { title: 'Data Integration', description: 'Inconsistent data contracts across systems.', relatedTools: ['workflow', 'assessment'] },
      { title: 'Cybersecurity Hygiene', description: 'Patch cycles for OT systems lag IT.', relatedTools: ['assessment', 'workflow'] },
    ],
    tools: ['assessment', 'workflow', 'intel-feed', 'scenarios'],
    gisLayers: [
      { id: 'fiber-network', name: 'Agency Fiber Network', color: '#1751E8' },
      { id: 'its-devices', name: 'ITS Devices', color: '#EBF0FF' },
    ],
    intelCards: [
      { title: 'Vuln Advisory', insight: 'Critical CVE affecting 3 vendor systems; patch window planned.', stat: '3', action: 'View Plan' },
      { title: 'Platform Adoption', insight: 'New data platform adoption +22% MoM.', stat: '+22%', action: 'Report' },
      { title: 'Modernization Milestone', insight: 'CRM modernization reached UAT last week.', stat: 'UAT', action: 'Review' },
    ],
    zilmPrompts: [
      'Summarize our technology modernization roadmap',
      'Identify systems approaching end-of-life within 24 months',
      'Draft a cybersecurity status brief for leadership',
      'Compare our data platform adoption against target',
    ],
  },

  'data-analytics': {
    key: 'data-analytics',
    name: 'Data & Analytics',
    icon: 'BarChart',
    color: 'var(--teal)',
    shortDescription:
      'Enterprise analytics, reporting, and decision intelligence',
    kpis: [
      { title: 'Certified Datasets', value: '148', unit: '', trend: 'up', trendPercent: 6 },
      { title: 'Dashboards in Use', value: '62', unit: '', trend: 'up', trendPercent: 4 },
      { title: 'Data Quality Score', value: '88', unit: '%', trend: 'up', trendPercent: 2 },
      { title: 'Analyst Queries / Day', value: '540', unit: '', trend: 'up', trendPercent: 9 },
    ],
    painPoints: [
      { title: 'Source Governance', description: 'Ambiguous ownership of source-of-truth datasets.', relatedTools: ['assessment', 'workflow'] },
      { title: 'Dashboard Sprawl', description: 'Many dashboards duplicate the same KPIs.', relatedTools: ['assessment', 'intel-brief'] },
      { title: 'Self-serve Enablement', description: 'Non-analyst staff struggle with ad-hoc questions.', relatedTools: ['intel-brief', 'workflow'] },
    ],
    tools: ['intel-brief', 'intel-feed', 'assessment', 'calculator'],
    gisLayers: [
      { id: 'data-coverage', name: 'Data Coverage Heat', color: '#0A8A65' },
      { id: 'sensor-network', name: 'Sensor Network', color: '#E0F5EE' },
    ],
    intelCards: [
      { title: 'Dataset Certified', insight: 'Traffic volumes 2024 certified as gold.', stat: '1', action: 'View' },
      { title: 'Dashboard Consolidation', insight: 'Identified 6 duplicate dashboards for retirement.', stat: '6', action: 'Plan' },
      { title: 'Self-serve Usage', insight: 'Self-serve queries up 34% QoQ.', stat: '+34%', action: 'Report' },
    ],
    zilmPrompts: [
      'Summarize our top 5 analytics use cases by value',
      'Identify candidate datasets for certification',
      'Draft a dashboard consolidation plan',
      'Compare self-serve vs analyst query volume',
    ],
  },

  auditing: {
    key: 'auditing',
    name: 'Auditing',
    icon: 'FileText',
    color: 'var(--green)',
    shortDescription:
      'Internal and external audit planning, execution, and remediation',
    kpis: [
      { title: 'Open Findings', value: '22', unit: '', trend: 'down', trendPercent: 11 },
      { title: 'Findings Past Due', value: '4', unit: '', trend: 'down', trendPercent: 20 },
      { title: 'Audits Completed YTD', value: '11', unit: 'of 18 planned', trend: 'up', trendPercent: 5 },
      { title: 'Avg Remediation Cycle', value: '62', unit: 'days', trend: 'down', trendPercent: 8 },
    ],
    painPoints: [
      { title: 'Evidence Collection', description: 'Evidence gathering consumes majority of audit effort.', relatedTools: ['workflow', 'assessment'] },
      { title: 'Finding Tracking', description: 'Findings tracked across multiple tools.', relatedTools: ['workflow', 'intel-brief'] },
      { title: 'Root Cause Quality', description: 'Root cause depth varies by auditor.', relatedTools: ['assessment', 'intel-brief'] },
    ],
    tools: ['workflow', 'assessment', 'intel-brief', 'calculator'],
    gisLayers: [
      { id: 'audit-scope', name: 'Audit Scope Areas', color: '#236809' },
    ],
    intelCards: [
      { title: 'Finding Past Due', insight: '4 findings past due more than 30 days.', stat: '4', action: 'Escalate' },
      { title: 'Audit Kickoff', insight: 'FHWA process review kickoff scheduled May 20.', stat: 'May 20', action: 'Prep' },
      { title: 'Remediation Win', insight: 'Procurement finding closed ahead of target.', stat: 'Closed', action: 'View' },
    ],
    zilmPrompts: [
      'Summarize open audit findings by severity',
      'Identify themes across recent audit findings',
      'Draft a status update for the audit committee',
      'Recommend remediation sequencing by impact',
    ],
  },
};

export const FUNCTION_KEYS = Object.keys(functions);

export function getFunction(key: string): FunctionDomain | undefined {
  return functions[key];
}
